import { Field, Form, Formik } from 'formik';
import { Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useUserOrg from '../../Authentication/components/hooks/useUserOrg';
import { useDeviceInvite } from './hooks/useDeviceInvite';
import { useDeviceGroupList } from './hooks/useDeviceGroupList';
import { Button } from '@/src/shadComponents/ui/button';
import { Input } from '@/src/shadComponents/ui/input';
import { Label } from '@/src/shadComponents/ui/label';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@/src/shadComponents/ui/card';

interface CreateDeviceInviteFormValues {
    name: string;
    groupId: number;
}

export default function CreateDeviceInvite() {
    const { orgId } = useUserOrg();
    const navigate = useNavigate();
    const { createDeviceInvite, error } = useDeviceInvite(orgId);
    const { deviceGroups, isLoading } = useDeviceGroupList(orgId);
    const initialValues: CreateDeviceInviteFormValues = {
        name: '',
        groupId: 0,
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="mb-6">
                <p className="text-sm text-muted-foreground">Create</p>
                <h1 className="mt-1 text-2xl font-bold tracking-tight text-foreground">
                    Add Device Invite
                </h1>
                <p className="mt-1 text-sm text-muted-foreground">
                    Create an invite for a device to be assigned to a device
                    group.
                </p>
            </div>

            {error && (
                <div className="mb-4 rounded-md bg-red-50 p-4">
                    <div className="text-sm text-red-700">{error}</div>
                </div>
            )}

            <Card>
                <CardHeader>
                    <CardTitle>Device Invite</CardTitle>
                    <p className="text-sm text-muted-foreground">
                        When registering a device, you will enter this name.
                        Once it has been used, it cannot be used again.
                    </p>
                </CardHeader>
                <CardContent>
                    <Formik
                        initialValues={initialValues}
                        onSubmit={async (values, actions) => {
                            const wasSuccessful =
                                await createDeviceInvite(values);
                            actions.setSubmitting(false);

                            if (wasSuccessful) {
                                navigate(-1);
                            }
                        }}
                    >
                        {(props) => (
                            <Form
                                className="space-y-6"
                                placeholder={undefined}
                                onPointerEnterCapture={undefined}
                                onPointerLeaveCapture={undefined}
                            >
                                <div className="space-y-2">
                                    <Label htmlFor="name">Device Name</Label>
                                    <Field
                                        as={Input}
                                        id="name"
                                        name="name"
                                        placeholder="Invite Name"
                                        className="max-w-md"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="groupId">
                                        Device Group
                                    </Label>
                                    <Field
                                        as="select"
                                        id="groupId"
                                        name="groupId"
                                        className="flex h-9 w-full max-w-md rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                    >
                                        <option value={0}>
                                            Select a Device Group
                                        </option>
                                        {deviceGroups.map((group) => (
                                            <option
                                                key={group.id}
                                                value={group.id}
                                            >
                                                {group.name}
                                            </option>
                                        ))}
                                    </Field>
                                </div>

                                <div className="flex items-center gap-2">
                                    <Button
                                        type="submit"
                                        disabled={props.isSubmitting}
                                    >
                                        Create Device Invite
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => navigate(-1)}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </CardContent>
            </Card>
        </div>
    );
}
